import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { setupAppPageBodyBackground } from "../game/gameStageBackground";
import {
  STAGE_ROW_KEY,
  ensureStageRowId,
  ensureStageSessionForUser,
  saveGameStageAndReport,
} from "../../functions/stageReportSync";
import StageTimer from "../game/StageTimer";
import {
  formatSecondsToClock,
  loadHallElapsedFromDb,
  persistHallElapsedTime,
  readTotalScoreFromStage,
  liveProgressPoints,
} from "../hall_1/hallTimer";
import {
  markHall2HotspotCompleted,
  unlockHall2Hotspot,
  getRedirectIfHall2StageDone,
} from "./heritageHallData";
import {
  ARTIFACTS,
  DESCRIPTIONS,
  ARCHIVE_POINTS,
  ARCHIVE_SUCCESS_COPY,
  ARCHIVE_UNLOCKS,
  shuffleArray,
  validateArchivePlacements,
  assignCard,
  clearArtifactSlot,
  unassignedCardIds,
} from "./museumArchiveData";
import { createDragAutoScroller } from "../hall_1/dragDropHelpers";
import deskBg from "./stage9/stage9_desk.jpg";
import mobBg from "./stage9/stage9_mob.jpg";
import displayBox from "./stage9/box.png";
/* Use hall_1 stage6 nameplate — proven drag-ghost text contrast */
import nameplateBg from "../hall_1/stage6/nameplate.png";
import letterBg from "../img/letter.png";
import continueBtn from "./stage9/continue.png";
import "../fonts/breuer-headline.css";
import "../game/stageSuccessLetter.css";
import "./stage9.css";

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function DescCard({ card, lifted, wrong, locked, onPointerDown, compact }) {
  const lines = String(card?.text || "").split("\n").filter(Boolean);
  return (
    <button
      type="button"
      className={`archive-card${lifted ? " archive-card--lifted" : ""}${
        wrong ? " archive-card--wrong" : ""
      }${locked ? " archive-card--locked" : ""}${
        compact ? " archive-card--compact" : ""
      }`}
      style={{ backgroundImage: `url(${nameplateBg})` }}
      onPointerDown={(e) => {
        if (locked) return;
        onPointerDown?.(e, card.id);
      }}
      aria-label={lines.join(", ")}
      aria-grabbed={lifted ? "true" : "false"}
      aria-disabled={locked ? "true" : undefined}
    >
      <span className="archive-card__text">
        {lines.map((line, i) => (
          <span key={`${card.id}-${i}`} className="archive-card__line">
            {line}
          </span>
        ))}
      </span>
    </button>
  );
}

/** Same pattern as hall_1/stage6 DragGhost — full 2-line name while dragging. */
function DragGhost({ card, x, y, width }) {
  if (!card) return null;
  const lines = String(card.text || "").split("\n").filter(Boolean);
  const ghostWidth = Math.max(width || 0, 280);
  return (
    <div
      className="archive-drag-ghost"
      style={{
        left: x,
        top: y,
        width: `${ghostWidth}px`,
        backgroundImage: `url(${nameplateBg})`,
      }}
      aria-hidden
    >
      <span className="archive-card__text">
        {lines.map((line, i) => (
          <span key={`ghost-${card.id}-${i}`} className="archive-card__line">
            {line}
          </span>
        ))}
      </span>
    </div>
  );
}

/** Hall 2 Stage 9 — Museum Archive (match descriptions to artifacts). */
export default function HallStage9() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);
  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = storedUser?.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const cardById = useMemo(
    () => Object.fromEntries(DESCRIPTIONS.map((c) => [c.id, c])),
    []
  );
  const artifactById = useMemo(
    () => Object.fromEntries(ARTIFACTS.map((a) => [a.id, a])),
    []
  );

  const [cardOrder] = useState(() =>
    shuffleArray(DESCRIPTIONS.map((c) => c.id))
  );
  const [placements, setPlacements] = useState(() =>
    Object.fromEntries(ARTIFACTS.map((a) => [a.id, null]))
  );
  const [lockedArtifactIds, setLockedArtifactIds] = useState([]);
  const [drag, setDrag] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [feedback, setFeedback] = useState({ wrong: [], correct: [] });
  const [status, setStatus] = useState("playing");
  const [errorMsg, setErrorMsg] = useState("");
  const [elapsedSec, setElapsedSec] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );

  const elapsedRef = useRef(0);
  const dragRef = useRef(null);
  const placementsRef = useRef(placements);
  const lockedRef = useRef([]);
  const completingRef = useRef(false);
  const autoScrollRef = useRef(null);
  if (!autoScrollRef.current) {
    autoScrollRef.current = createDragAutoScroller();
  }

  useEffect(() => {
    placementsRef.current = placements;
  }, [placements]);

  useEffect(() => {
    lockedRef.current = lockedArtifactIds;
  }, [lockedArtifactIds]);

  const liveStagePoints = useMemo(() => {
    const correctCount = ARTIFACTS.filter(
      (a) => placements[a.id] === a.correctCardId
    ).length;
    return liveProgressPoints(correctCount, ARTIFACTS.length, ARCHIVE_POINTS);
  }, [placements]);

  const displayPoints =
    status === "success" ? totalScore : totalScore + liveStagePoints;

  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/hall-2"));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("hall2-stage9-page");
    const cleanupBg = setupAppPageBodyBackground(document.body, {
      deskForeground: deskBg,
      mobForeground: mobBg,
    });
    return () => {
      cleanupBg?.();
      document.body.classList.remove("hall2-stage9-page");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      ensureStageSessionForUser(storedUser);
      const { stageId, elapsedSec: sec, stage } = await loadHallElapsedFromDb({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      const redirect = getRedirectIfHall2StageDone(stage, 9);
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }

      if (stageId) setStageRowId(String(stageId));
      elapsedRef.current = sec;
      setElapsedSec(sec);
      setTotalScore(readTotalScoreFromStage(stage));
      setTimerReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, storedUser, isDemoBypass, navigate]);

  useEffect(() => {
    if (!timerReady || status === "success") return undefined;
    const tick = window.setInterval(() => {
      setElapsedSec((s) => {
        const next = s + 1;
        elapsedRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [timerReady, status]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const save = () => {
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
        elapsedSec: elapsedRef.current,
      });
    };
    const id = window.setInterval(save, 15000);
    return () => {
      clearInterval(id);
      if (status === "playing") save();
    };
  }, [
    timerReady,
    backendBase,
    adminToken,
    storedUser,
    isDemoBypass,
    stageRowId,
    status,
  ]);

  const poolIds = useMemo(() => {
    const free = new Set(unassignedCardIds(placements));
    return cardOrder.filter((id) => free.has(id));
  }, [placements, cardOrder]);

  const clearDrag = useCallback(() => {
    autoScrollRef.current?.stop();
    setDrag(null);
    setDropTargetId(null);
    document.body.classList.remove("archive-dragging");
  }, []);

  useEffect(() => () => clearDrag(), [clearDrag]);

  const lockCorrectArtifacts = useCallback((nextPlacements) => {
    const toLock = ARTIFACTS.filter(
      (a) =>
        nextPlacements[a.id] === a.correctCardId &&
        !lockedRef.current.includes(a.id)
    ).map((a) => a.id);
    if (!toLock.length) return;
    setLockedArtifactIds((prev) => {
      const set = new Set(prev);
      toLock.forEach((id) => set.add(id));
      return [...set];
    });
  }, []);

  const onPointerDownCard = useCallback(
    (e, cardId) => {
      if (status === "success" || status === "saving") return;
      if (e.button != null && e.button !== 0) return;

      let originArtifact = null;
      Object.entries(placementsRef.current).forEach(([artifactId, cid]) => {
        if (cid === cardId) originArtifact = artifactId;
      });
      // Correct matches are locked — cannot pick up again
      if (originArtifact && lockedRef.current.includes(originArtifact)) return;

      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();

      e.preventDefault();
      el.setPointerCapture?.(e.pointerId);

      // Keep card in slot while dragging so pointer capture isn't lost (cursor bug).
      setDrag({
        cardId,
        originArtifact,
        pointerId: e.pointerId,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        x: rect.left,
        y: rect.top,
        width: Math.max(rect.width, 280),
      });
      setFeedback({ wrong: [], correct: [] });
      setErrorMsg("");
      document.body.classList.add("archive-dragging");
      autoScrollRef.current?.update(e.clientY);
    },
    [status]
  );

  const tryDropOnArtifact = useCallback(
    (cardId, artifactId, originArtifact) => {
      const artifact = artifactById[artifactId];
      if (!artifact) {
        clearDrag();
        return;
      }

      // Cannot drop onto an already-correct locked slot
      if (lockedRef.current.includes(artifactId)) {
        clearDrag();
        return;
      }

      if (originArtifact === artifactId) {
        clearDrag();
        return;
      }

      const next = assignCard(
        placementsRef.current,
        artifactId,
        cardId,
        originArtifact
      );
      placementsRef.current = next;
      setPlacements(next);
      lockCorrectArtifacts(next);
      setFeedback({ wrong: [], correct: [] });
      setErrorMsg("");
      clearDrag();
    },
    [artifactById, clearDrag, lockCorrectArtifacts]
  );

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;

      autoScrollRef.current?.update(e.clientY);

      setDrag((prev) =>
        prev
          ? {
              ...prev,
              x: e.clientX - prev.offsetX,
              y: e.clientY - prev.offsetY,
            }
          : prev
      );

      const el = document.elementFromPoint(e.clientX, e.clientY);
      const slotEl = el?.closest?.("[data-artifact-slot]");
      setDropTargetId(slotEl?.getAttribute("data-artifact-slot") || null);
    };

    const onUp = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;

      autoScrollRef.current?.stop();

      const el = document.elementFromPoint(e.clientX, e.clientY);
      const slotEl = el?.closest?.("[data-artifact-slot]");
      const poolEl = el?.closest?.("[data-archive-pool]");

      if (slotEl) {
        tryDropOnArtifact(
          d.cardId,
          slotEl.getAttribute("data-artifact-slot"),
          d.originArtifact
        );
      } else if (poolEl) {
        if (d.originArtifact && !lockedRef.current.includes(d.originArtifact)) {
          setPlacements((prev) => clearArtifactSlot(prev, d.originArtifact));
        }
        clearDrag();
      } else {
        // Cancel — card stays in origin slot (never cleared on pick-up)
        clearDrag();
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      autoScrollRef.current?.stop();
      clearDrag();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [tryDropOnArtifact, clearDrag]);

  const handleSubmit = useCallback(async () => {
    if (status === "success" || status === "saving" || completingRef.current) {
      return;
    }

    const result = validateArchivePlacements(placements);
    if (!result.ok) {
      setFeedback({
        wrong: [...result.wrongIds, ...result.missingIds],
        correct: ARTIFACTS.filter(
          (a) =>
            placements[a.id] &&
            !result.wrongIds.includes(a.id) &&
            !result.missingIds.includes(a.id)
        ).map((a) => a.id),
      });
      setErrorMsg(
        result.missingIds.length
          ? "Place a description under every artifact, then try again."
          : "Some descriptions don’t match. Try again."
      );
      return;
    }

    completingRef.current = true;
    setFeedback({
      wrong: [],
      correct: ARTIFACTS.map((a) => a.id),
    });
    setErrorMsg("");
    setStatus("saving");

    const timeStr = formatSecondsToClock(elapsedRef.current);
    const answers = {
      puzzle: "museum_archive",
      placements,
    };

    try {
      let stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId && !isDemoBypass && backendBase && adminToken) {
        stageId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
        });
        if (stageId) setStageRowId(String(stageId));
      }

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: 9,
          score: ARCHIVE_POINTS,
          answers,
          time: timeStr,
        });
        if (!saveResult?.ok) {
          throw new Error("stage9 save failed");
        }
        if (saveResult.total_score != null) {
          setTotalScore(Number(saveResult.total_score) || 0);
        } else {
          setTotalScore((prev) => prev + ARCHIVE_POINTS);
        }
      } else {
        setTotalScore((prev) => prev + ARCHIVE_POINTS);
      }

      markHall2HotspotCompleted("museum_archive");
      unlockHall2Hotspot(ARCHIVE_UNLOCKS);
      setStatus("success");
    } catch (err) {
      console.error("Museum archive save:", err);
      completingRef.current = false;
      setStatus("playing");
      setErrorMsg("Could not save progress. Please try again.");
    }
  }, [
    status,
    placements,
    stageRowId,
    isDemoBypass,
    backendBase,
    adminToken,
    storedUser,
  ]);

  const dragCard = drag ? cardById[drag.cardId] : null;
  const locked = status === "success" || status === "saving";

  return (
    <div
      className={`hall2-stage9 archive-play${
        status === "success" ? " archive-play--success" : ""
      }`}
    >
      <div className="stage-escape-hud">
        <StageTimer
          timeLabel={formatSecondsToClock(elapsedSec)}
          points={displayPoints}
        />
      </div>

      <div className="archive-play__inner">
        <header className="archive-play__header">
          <h1 className="archive-play__title">MUSEUM ARCHIVE</h1>
          <p className="archive-play__subtitle">
            Match every artifact with its correct information card.
          </p>
          <div className="archive-play__rule" aria-hidden>
            <span />
          </div>
        </header>

        <div className="archive-play__cases" role="list">
          {ARTIFACTS.map((artifact) => {
            const assignedId = placements[artifact.id];
            const assigned = assignedId ? cardById[assignedId] : null;
            const isLockedSlot = lockedArtifactIds.includes(artifact.id);
            const isOver =
              dropTargetId === artifact.id && drag && !isLockedSlot;
            const isWrong = feedback.wrong.includes(artifact.id);
            const isCorrect =
              isLockedSlot || feedback.correct.includes(artifact.id);

            return (
              <div
                key={artifact.id}
                role="listitem"
                data-artifact-slot={artifact.id}
                className={`archive-column${isOver ? " archive-column--over" : ""}${
                  isWrong ? " archive-column--wrong" : ""
                }${isCorrect ? " archive-column--correct" : ""}`}
              >
                <div
                  className="archive-case"
                  style={{ backgroundImage: `url(${displayBox})` }}
                >
                  <img
                    src={artifact.image}
                    alt={artifact.label}
                    className="archive-case__art"
                    draggable={false}
                  />
                </div>
                <div className="archive-column__slot">
                  {assigned ? (
                    <DescCard
                      card={assigned}
                      compact
                      locked={locked || isLockedSlot}
                      lifted={drag?.cardId === assigned.id}
                      wrong={isWrong}
                      onPointerDown={onPointerDownCard}
                    />
                  ) : (
                    <span className="archive-column__slot-empty">
                      Drop description
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {status !== "success" ? (
          <>
            <div className="archive-play__pool" data-archive-pool="1">
              {poolIds.map((id) => (
                <DescCard
                  key={id}
                  card={cardById[id]}
                  locked={locked}
                  lifted={drag?.cardId === id}
                  onPointerDown={onPointerDownCard}
                />
              ))}
            </div>

            {errorMsg ? (
              <p className="archive-play__error" role="alert">
                {errorMsg}
              </p>
            ) : null}

            <button
              type="button"
              className="archive-play__submit"
              style={{ backgroundImage: `url(${nameplateBg})` }}
              disabled={status === "saving"}
              onClick={handleSubmit}
            >
              {status === "saving" ? "SAVING…" : "RESTORE ARCHIVE"}
            </button>
          </>
        ) : null}
      </div>

      {status === "success" ? (
        <div
          className="archive-play__success"
          role="dialog"
          aria-modal="true"
          aria-labelledby="archive-success-title"
        >
          <div className="archive-play__success-veil" aria-hidden />
          <aside
            className="archive-play__success-card stage-success-letter"
            style={{ backgroundImage: `url(${letterBg})` }}
          >
            <h2
              id="archive-success-title"
              className="archive-play__success-title"
            >
              {ARCHIVE_SUCCESS_COPY.title}
            </h2>
            <div
              className="archive-play__rule archive-play__success-rule"
              aria-hidden
            >
              <span />
            </div>
            <p className="archive-play__success-body">
              {ARCHIVE_SUCCESS_COPY.body}
            </p>
            <button
              type="button"
              className="archive-play__success-cta"
              style={{ backgroundImage: `url(${continueBtn})` }}
              onClick={() => navigate("/hall-2")}
            >
              CONTINUE
            </button>
          </aside>
        </div>
      ) : null}

      {drag && dragCard ? (
        <DragGhost
          card={dragCard}
          x={drag.x}
          y={drag.y}
          width={drag.width}
        />
      ) : null}
    </div>
  );
}
